import { Component, EventEmitter, Input, Output } from '@angular/core';
import { Principal } from '../../../../shared';
import { BreedingMethodFilterTypeEnum } from '../../../../shared/breeding-methods-select/breeding-methods-select.component';
import { ValueReference } from '../../../../shared/model/observation-variable.model';
import { BreedingMethod } from '../../../../shared/breeding-method/model/breeding-method';
import { DatasetService } from '../../../../shared/dataset/service/dataset.service';
import { ObservationUnitsSearchFilter } from '../../../../shared/dataset/model/observation-units-search-request.model';
import { DateFormatEnum, formatDate } from '../../../../shared/util/date-utils';
import { AlertService } from '../../../../shared/alert/alert.service';
import { TranslateService } from '@ngx-translate/core';
import { StudyEditorContext } from '../../../study-editor.context';
import { ModalConfirmComponent } from '../../../../shared/modal/modal-confirm.component';
import { NgbDateAdapter, NgbDateParserFormatter, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import {
    MANAGE_ACCEPT_PENDING_OBSERVATION_VALUES_PERMISSIONS,
    MANAGE_CONFIRMED_OBSERVATION_VALUES_PERMISSIONS,
    MANAGE_PENDING_OBSERVATION_VALUES_PERMISSIONS
} from '../../../../shared/auth/permissions';
import { isNumeric } from '../../../../shared/util/is-numeric';
import { ColumnData } from '../../../shared/models/column-data.model';
import { CustomStringDateAdapter } from '../../../shared/adapters/custom-string-date-adapter';
import { CustomDateParserFormatter } from '../../../shared/adapters/custom-date-parser-formatter';

interface DropdownValue {
    id: string;
    name: string;
    description: string;
}

@Component({
    selector: 'jhi-variable-batch-action',
    templateUrl: './variable-batch-action.component.html',
    styleUrl: './variable-batch-action.component.scss',
    providers: [
        { provide: NgbDateAdapter, useClass: CustomStringDateAdapter },
        { provide: NgbDateParserFormatter, useClass: CustomDateParserFormatter }
    ]
})
export class VariableBatchActionComponent {

    protected readonly MANAGE_PENDING_OBSERVATION_VALUES_PERMISSIONS = MANAGE_PENDING_OBSERVATION_VALUES_PERMISSIONS;
    protected readonly MANAGE_CONFIRMED_OBSERVATION_VALUES_PERMISSIONS = MANAGE_CONFIRMED_OBSERVATION_VALUES_PERMISSIONS;
    protected readonly MANAGE_ACCEPT_PENDING_OBSERVATION_VALUES_PERMISSIONS = MANAGE_ACCEPT_PENDING_OBSERVATION_VALUES_PERMISSIONS;

    BREEDING_METHOD_VTE = 8252;
    protected readonly BreedingMethodFilterTypeEnum = BreedingMethodFilterTypeEnum;

    @Input() variables: ColumnData[];
    @Input() datasetId: number;
    @Input() instanceIds: number[];
    @Input() isPendingView: boolean;
    @Input() filter: ObservationUnitsSearchFilter;
    @Input() isCategoricalDescriptionView: boolean = false;
    @Output() selectedBatchVariable = new EventEmitter<ColumnData>();
    @Output() onApplySuccess = new EventEmitter<void>();
    selectedVariable: ColumnData;
    selectedBatchAction: number;
    value: any;
    categoricalItems: DropdownValue[] = [];

    constructor(
        public principal: Principal,
        private studyEditorContext: StudyEditorContext,
        private datasetService: DatasetService,
        private alertService: AlertService,
        private translateService: TranslateService,
        private modalService: NgbModal) {
    }

    hasAnyAuthority = this.principal.hasAnyAuthorityDirect;

    isBreedingMethod() {
        return this.selectedVariable.termId === this.BREEDING_METHOD_VTE;
    }

    convertToOptionDataItems(possibleValues: ValueReference[]): DropdownValue[] {
        if (possibleValues) {
            return possibleValues.map(possibleValue => ({
                id: possibleValue.id.toString(),
                name: possibleValue.name,
                description: possibleValue.description
            }));
        }
        return [];
    }

    onMethodChanged($event: BreedingMethod) {
        this.value = $event.code;
    }

    onVariableChange(column: ColumnData) {
        if (column) {
            this.categoricalItems = this.convertToOptionDataItems(column.possibleValues);
        }
        this.value = null;
        this.selectedBatchVariable.emit(column);
    }

    applyBatchAction() {

        const studyId = this.studyEditorContext.studyDetails.id;

        if (this.selectedBatchAction === 1) {
            var validated = this.newValueBatchUpdateValidation();
            if (!validated) {
                return;
            }
        }

        const filter = this.filter;
        if (this.selectedBatchAction === 3) {
            filter.variableHasValue = true;
        }
        const param = {
            instanceIds: this.instanceIds,
            draftMode: this.isPendingView,
            filter: filter
        };
        this.datasetService.countFilteredPhenotypesAndInstances(studyId, this.datasetId, param).toPromise().then((response: any) => {
            const message = this.translateService.instant('study-editor.study-dataset.variable-batch-action.confirm-batch-action', {
                param1: response.totalFilteredPhenotypes,
                param2: response.totalFilteredInstances
            });

            const confirmModalRef = this.modalService.open(ModalConfirmComponent as Component);
            confirmModalRef.componentInstance.message = message;
            confirmModalRef.componentInstance.confirmLabel = this.translateService.instant('study-editor.study-dataset.variable-batch-action.proceed');
            confirmModalRef.result.then((result) => {
                switch (this.selectedBatchAction) {
                case 1:
                    this.applyNewValue();
                    break;
                case 2:
                    this.acceptValueAsIs();
                    break;
                case 3:
                    this.deleteObservationsValues();
                default:
                    break;
                }
            });
        }, () => {
            this.alertService.error('error.general');
        });;
    };

    newValueBatchUpdateValidation() {
        if (this.selectedVariable.dataTypeCode === 'D') {
            var newValue = formatDate(this.value, DateFormatEnum.ISO_8601_NUMBER);
            if (!newValue) {
                this.alertService.error('study-editor.study-dataset.variable-batch-action.invalid-date');
                return false;
            }
        }

        if (this.selectedVariable.dataTypeCode === 'N') {
            const newValue = this.value;
            if (this.selectedVariable.minRange && this.selectedVariable.maxRange && //
                (newValue < this.selectedVariable.minRange || newValue > this.selectedVariable.maxRange)) {
                this.alertService.error('study-editor.study-dataset.variable-batch-action.number-out-of-range');
            }
            return true;
        }
        return true;
    }

    applyNewValue() {

        const studyId = this.studyEditorContext.studyDetails.id;

        let newValue = this.value;
        if (this.selectedVariable.dataTypeCode === 'D') {
            newValue = formatDate(newValue, DateFormatEnum.ISO_8601_NUMBER);
        }
        const param = {
            newValue: newValue,
            newCategoricalValueId: this.getCategoricalValueId(this.value, this.selectedVariable),
            observationUnitsSearchDTO: {
                instanceIds: this.instanceIds,
                draftMode: this.isPendingView,
                filter: this.filter
            }
        };
        this.datasetService.setValueToVariable(studyId, this.datasetId, param).toPromise().then(() => {
            this.reset();
            this.onApplySuccess.emit();
        });
    }

    acceptValueAsIs() {

        const studyId = this.studyEditorContext.studyDetails.id;

        // acceptDraftDataByVariable
        const param = {
            instanceIds: this.instanceIds,
            draftMode: this.isPendingView,
            filter: this.filter
        };
        this.datasetService.acceptDraftDataByVariable(studyId, this.datasetId, param).toPromise().then(() => {
            this.reset();
            this.onApplySuccess.emit();
        });
    }

    deleteObservationsValues() {

        const studyId = this.studyEditorContext.studyDetails.id;

        var param = {
            instanceIds: this.instanceIds,
            draftMode: this.isPendingView,
            filter: this.filter,
        };
        this.datasetService.deleteVariableValues(studyId, this.datasetId, param).toPromise().then(() => {
            this.reset();
            this.onApplySuccess.emit();
        });
    }

    protected getCategoricalValueId(value: string, columnData: ColumnData): any | null {
        if (columnData.possibleValues && value !== 'missing') {
            let possibleValue = columnData.possibleValues.find((possibleValue) => {
                return possibleValue.name === value;
            });
            if (possibleValue) {
                return possibleValue.id;
            }
            // TODO: Improve this logic
            if (value && isNumeric(value)) {
                return value;
            }
        }
        return null;
    }

    reset() {
        this.selectedVariable = null;
        this.selectedBatchAction = 1;
        this.value = null;
        this.categoricalItems = [];
        this.selectedBatchVariable.emit(null);
    }
}
